import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Visibility as VisibilityIcon,
  PhotoLibrary as PhotoLibraryIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import PropertyDeleteButton from '../components/Properties/PropertyDeleteButton';

const EditProperties = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showImageDialog, setShowImageDialog] = useState(false);

  useEffect(() => {
    fetchUserProperties();
  }, [user]);

  const fetchUserProperties = async () => {
    try {
      setLoading(true);
      
      if (!user?.email) {
        setError('User not authenticated');
        return;
      }

      console.log('=== EDIT PROPERTIES DEBUG START ===');
      console.log('1. User email:', user.email);
      console.log('2. User ID (UUID):', user.id);

      // Get user from custom database
      const { data: customUser, error: userError } = await supabase
        .from('users')
        .select('id, email')
        .eq('email', user.email)
        .single();

      console.log('3. Custom user lookup result:', customUser);
      console.log('4. Custom user error:', userError);

      if (userError) {
        console.error('Failed to find user account:', userError);
        setError('Failed to find user account');
        return;
      }

      console.log('5. Custom user ID (integer):', customUser.id);

      // First try to get properties with integer owner_id (primary method)
      const { data: integerProperties, error: integerError } = await supabase
        .from('properties')
        .select('*')
        .eq('owner_id', customUser.id)
        .order('created_at', { ascending: false });

      console.log('6. Integer-based properties:', integerProperties);
      console.log('7. Integer query error:', integerError);

      let uniqueProperties = integerProperties || [];

      // Only try UUID query if integer query found no properties and there was no error
      if ((!integerProperties || integerProperties.length === 0) && !integerError) {
        console.log('8. No integer properties found, trying UUID query...');
        
        const { data: uuidProperties, error: uuidError } = await supabase
          .from('properties')
          .select('*')
          .eq('owner_id', user.id)
          .order('created_at', { ascending: false });

        console.log('9. UUID-based properties:', uuidProperties);
        console.log('10. UUID query error:', uuidError);

        if (uuidProperties && uuidProperties.length > 0) {
          uniqueProperties = [...uniqueProperties, ...uuidProperties];
          
          // Remove duplicates based on property ID
          uniqueProperties = uniqueProperties.filter((property, index, self) => 
            index === self.findIndex(p => p.id === property.id)
          );
        }
      } else {
        console.log('8. Using integer-based properties only');
      }

      console.log('11. Final properties:', uniqueProperties);
      console.log('12. Total properties found:', uniqueProperties.length);
      console.log('=== EDIT PROPERTIES DEBUG END ===');

      setProperties(uniqueProperties);
      setError('');
    } catch (err) {
      console.error('Error fetching properties:', err);
      setError('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'for-sale': return 'success';
      case 'for-rent': return 'info';
      case 'sold': return 'error';
      case 'rented': return 'warning';
      default: return 'default';
    }
  };

  const handleEditProperty = (propertyId) => {
    navigate(`/edit-property/${propertyId}`);
  };

  const handleViewProperty = (propertyId) => {
    navigate(`/properties/${propertyId}`);
  };

  const handleManageImages = (property) => {
    setSelectedProperty(property);
    setShowImageDialog(true);
  };

  const handlePropertyDeleted = (deletedPropertyId) => {
    setProperties(prev => prev.filter(prop => prop.id !== deletedPropertyId));
  };

  if (loading) {
    return (
      <Container sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/dashboard')}
          sx={{ mb: 2 }}
        >
          Back to Dashboard
        </Button>
        
        <Typography variant="h4" gutterBottom fontWeight={600}>
          Edit Properties
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your property listings - edit details, arrange images, or delete properties
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {properties.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Properties Found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            You haven't added any properties yet.
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => navigate('/add-property')}
            sx={{ px: 4, py: 1.5 }}
          >
            Add Your First Property
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {properties.map((property) => (
            <Grid item xs={12} sm={6} md={4} key={property.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardMedia
                  component="img"
                  height="200"
                  image={property.images?.[0] || '/placeholder-property.jpg'}
                  alt={property.title}
                  sx={{ objectFit: 'cover' }}
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="h6" component="h3" gutterBottom>
                      {property.title}
                    </Typography>
                    <Chip 
                      label={property.status?.replace('-', ' ').toUpperCase() || 'N/A'} 
                      color={getStatusColor(property.status)}
                      size="small"
                    />
                  </Box>
                  
                  <Typography variant="h6" color="primary" sx={{ mb: 1, fontWeight: 600 }}>
                    {formatPrice(property.price)}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    📍 {property.address}, {property.city}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary">
                    🏠 {property.property_type} • 📐 {property.area} acres
                  </Typography>
                </CardContent>
                
                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleEditProperty(property.id)}
                    sx={{ mr: 1 }}
                  >
                    Edit
                  </Button>
                  
                  <IconButton
                    size="small"
                    onClick={() => handleViewProperty(property.id)}
                    title="View Property"
                  >
                    <VisibilityIcon />
                  </IconButton>
                  
                  <IconButton
                    size="small"
                    onClick={() => handleManageImages(property)}
                    title="Manage Images"
                  >
                    <PhotoLibraryIcon />
                  </IconButton>
                  
                  <PropertyDeleteButton 
                    property={property} 
                    onDelete={handlePropertyDeleted}
                  />
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Image Management Dialog (Coming Soon) */}
      <Dialog 
        open={showImageDialog} 
        onClose={() => setShowImageDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Manage Images: {selectedProperty?.title}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              Image management feature coming soon!
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This will allow you to reorder images, add new ones, and set the main property photo.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowImageDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EditProperties;
